// apps/landing-page/src/components/waitlist/WaitlistReveal.tsx
'use client';

import { useState } from 'react';
import { useTranslations } from 'next-intl';
import { Button } from '@/components/ui';
import { WaitlistForm } from './WaitlistForm';

interface WaitlistRevealProps {
  /** i18n namespace holding formTitle/formDesc — shared shape with ComingSoon's namespace prop. */
  namespace: string;
  feature: string;
  source: string;
  /** Human product name, forwarded to WaitlistForm for the confirmation message. */
  product: string;
}

export function WaitlistReveal({ namespace, feature, source, product }: WaitlistRevealProps) {
  const t = useTranslations(namespace);
  const [open, setOpen] = useState(false);
  const [revealed, setRevealed] = useState(false);

  function handleOpen() {
    setOpen(true);
    // Defer to next frame so the transition classes actually animate in.
    requestAnimationFrame(() => setRevealed(true));
  }

  // Collapsed: the section's whole job is this one action, so lead with the
  // pitch and let the button size to its label. `w-full sm:w-auto` per
  // DESIGN_GUIDELINES §6.2 — this was previously `w-full justify-center`, which
  // stretched the CTA to a banner on desktop.
  if (!open) {
    return (
      <div className="max-w-xl mx-auto text-center">
        <h2 className="text-slate-900 font-bold text-xl sm:text-2xl mb-2">{t('formTitle')}</h2>
        <p className="text-slate-600 text-sm mb-6">{t('formDesc')}</p>
        <Button variant="primary" size="lg" onClick={handleOpen} className="w-full sm:w-auto justify-center">
          {t('formCta')}
        </Button>
      </div>
    );
  }

  return (
    <div
      className={`bg-white rounded-2xl p-5 sm:p-7 shadow-xl shadow-black/10 max-w-md mx-auto text-left transition-all duration-300 ease-out ${
        revealed ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-2'
      }`}
    >
      <h2 className="text-slate-800 font-bold text-base mb-1">{t('formTitle')}</h2>
      <p className="text-slate-500 text-xs mb-4">{t('formDesc')}</p>
      <WaitlistForm feature={feature} source={source} product={product} />
    </div>
  );
}
